import { useMemo, useState } from 'react'
import { motion } from 'framer-motion'
import { Badge } from '../ui'
import {
  DemoHeader,
  InsightCard,
  OntologyResponsePanel,
  UserActionPanel,
} from './GoalDrivenDemo'
import { useT } from '../../lib/i18n'
import type { ChangePropagationLocale } from '../../lib/locales'
import type { LabDemoProps } from './types'

type Status = 'standard' | 'watch' | 'sour'

function classify(h2sPpm: number, threshold: number): Status {
  if (h2sPpm > threshold) return 'sour'
  if (h2sPpm > 0.5 * threshold) return 'watch'
  return 'standard'
}

export function ChangePropagationDemo({ paradigm, lab, data }: LabDemoProps) {
  const t = useT()
  const demo = (data as typeof t.labDemos.changePropagation | undefined) ?? t.labDemos.changePropagation
  const baselineThreshold = demo.currentThresholdPpm
  const [threshold, setThreshold] = useState<number>(baselineThreshold)

  const rows = useMemo(() => {
    return demo.assets.map((a) => {
      const baseline = classify(a.h2sPpm, baselineThreshold)
      const current = classify(a.h2sPpm, threshold)
      return { asset: a, baseline, current, flipped: baseline !== current }
    })
  }, [demo.assets, baselineThreshold, threshold])

  const flippedCount = rows.filter((r) => r.flipped).length

  const summary = demo.impactSummaryTpl
    .replace('{affected}', String(flippedCount))
    .replace('{total}', String(rows.length))

  return (
    <div className="space-y-5">
      <DemoHeader paradigm={paradigm} />

      <div className="flex flex-col gap-4">
        <UserActionPanel
          label={lab.userActionLabel}
          title={demo.controlLabel}
          hint={demo.controlHint}
        >
          <div className="space-y-3">
            <div className="flex items-baseline justify-between gap-3 flex-wrap">
              <div className="flex items-baseline gap-4">
                <div>
                  <div className="mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-haze)] mb-0.5">
                    {demo.currentLabel}
                  </div>
                  <div className="mono text-[length:var(--text-body)] text-[color:var(--color-orion-text)]">
                    {baselineThreshold.toLocaleString()} {demo.unitLabel}
                  </div>
                </div>
                <span className="mono text-[color:var(--color-orion-haze)] self-end pb-1">→</span>
                <div>
                  <div className="mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-amber)] mb-0.5">
                    {demo.proposedLabel}
                  </div>
                  <div className="mono text-[length:var(--text-body)] text-[color:var(--color-orion-amber)] font-medium">
                    {threshold.toLocaleString()} {demo.unitLabel}
                  </div>
                </div>
              </div>
              {threshold !== baselineThreshold && (
                <button
                  type="button"
                  onClick={() => setThreshold(baselineThreshold)}
                  className="mono text-[length:var(--text-micro)] uppercase tracking-widest px-3 py-1.5 rounded-[var(--radius-sm)] border-faint text-[color:var(--color-orion-haze)] hover:text-[color:var(--color-orion-text)] hover:bg-[color:var(--color-orion-night)] transition-colors"
                >
                  {demo.resetButton}
                </button>
              )}
            </div>

            <input
              type="range"
              min={demo.thresholdRange.min}
              max={demo.thresholdRange.max}
              step={demo.thresholdRange.step}
              value={threshold}
              onChange={(e) => setThreshold(Number(e.target.value))}
              aria-label={demo.thresholdLabel}
              className="w-full accent-[color:var(--color-orion-amber)]"
            />
            <div className="flex justify-between mono text-[length:var(--text-micro)] text-[color:var(--color-orion-haze)]">
              <span>{demo.thresholdRange.min} {demo.unitLabel}</span>
              <span>{demo.thresholdRange.max} {demo.unitLabel}</span>
            </div>
          </div>
        </UserActionPanel>

        <OntologyResponsePanel label={lab.ontologyResponseLabel}>
          <div className="space-y-3">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-amber)]">
                {demo.impactTitle}
              </span>
              <Badge tone={flippedCount > 0 ? 'warn' : 'pass'}>{summary}</Badge>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {rows.map((row) => (
                <AssetRow key={row.asset.id} row={row} labels={demo} />
              ))}
            </div>
          </div>
        </OntologyResponsePanel>
      </div>

      <InsightCard title={demo.insightTitle} body={demo.insightBody} />
    </div>
  )
}

const STATUS_TONE: Record<Status, 'pass' | 'warn' | 'block'> = {
  standard: 'pass',
  watch: 'warn',
  sour: 'block',
}

function AssetRow({
  row,
  labels,
}: {
  row: {
    asset: ChangePropagationLocale['assets'][number]
    baseline: Status
    current: Status
    flipped: boolean
  }
  labels: ChangePropagationLocale
}) {
  return (
    <motion.div
      layout
      animate={
        row.flipped
          ? {
              boxShadow: [
                '0 0 0 1px oklch(75% 0.16 65 / 0)',
                '0 0 0 2px oklch(75% 0.16 65 / 0.55)',
                '0 0 0 1px oklch(75% 0.16 65 / 0.25)',
              ],
            }
          : { boxShadow: '0 0 0 0 transparent' }
      }
      transition={{ duration: 0.6 }}
      className={`rounded-[var(--radius-md)] p-3 border ${
        row.flipped
          ? 'border-[color:var(--color-orion-amber)]/55 bg-[color:var(--color-orion-amber)]/8'
          : 'border-[color:var(--color-orion-surface)]/40 bg-[color:var(--color-orion-deep)]/30'
      }`}
    >
      <div className="flex items-baseline justify-between gap-2 mb-1">
        <span className="mono text-[length:var(--text-small)] text-[color:var(--color-orion-text)]">
          {row.asset.code}
        </span>
        <Badge tone={STATUS_TONE[row.current]}>{labels.statusLabels[row.current]}</Badge>
      </div>
      <p
        className="text-[color:var(--color-orion-haze)] leading-snug mb-1.5"
        style={{ fontSize: 'var(--text-small)' }}
      >
        {row.asset.name}
      </p>
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <span className="mono text-[length:var(--text-micro)] text-[color:var(--color-orion-haze)]">
          {labels.assetMetaLabel}: {row.asset.h2sPpm.toLocaleString()} {labels.unitLabel}
        </span>
        {row.flipped && (
          <span className="mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-amber)]">
            {labels.flipBadge} · {labels.statusLabels[row.baseline]} → {labels.statusLabels[row.current]}
          </span>
        )}
      </div>
    </motion.div>
  )
}
